import { Injectable, signal, inject } from '@angular/core';
import { badgeSeverity } from '../../../shared/components/badge/badge.component';
import { StatusSeverity } from '../../../shared/custom/statusSeverity';
import { AutomaticJobDispatcherNotificationType } from '../../../shared/enums/automaticJobDispatcherNotificationType';
import { EquipmentsNeed, getEquipmentsNeedDescription, RecurrenceDay } from '../../../shared/enums/common';
import { RideCargoExtraFields, RideDriverExtraFields, RideJobExtraFields, RidesExtraFields, RideStopExtraFields } from '../../../shared/enums/extraFields';
import { RideAttentionStatus, RideAttentionStatusDescription } from '../../../shared/enums/rideAttentionStatus';
import { GetRideStatusSeverity, RideStatusDescription, RideStatusType } from '../../../shared/enums/rideStatusType';
import { rideStopCargoStatus, StopCargoStatusDescription } from '../../../shared/enums/rideStopCargoStatus';
import { RideStopStatus, RideStopStatusType, StopArrivalTimeStatus } from '../../../shared/enums/rideStopStatusType';
import { DriverLookup } from '../../../shared/interfaces/driver-lookup.interface';
import {
  RideCargoDocumentDto,
  RideCargoExtraFieldDocumentDto,
  RideDocumentDto,
  RideDriverDocumentDto,
  RidePassengerEquipmentDocumentDto,
  RideStopCargoDocumentDto,
  RideStopDocumentDto,
  RideStopExtraFieldDocumentDto,
} from '../../../shared/interfaces/ride-firestore';
import { ClientDetails, ContractorBasicInfo, ContractorDriverProviderContact, DriverDetails, IContractorDialog, MonitorDetails, RideInfo, RideListItem, RideStudent, RideTime } from '../../../shared/interfaces/ride-list-item';
import { IStep, IStepCargoes } from '../../../shared/interfaces/step.interface';
import { getLanguage } from '../../../shared/utilities/utility-functions';
import { Timestamp } from 'firebase/firestore';
import moment from 'moment';
import { InfoCardItem } from '../components/info-card/info-card.component';
import { ScheduleTimeAdjustmentsDocumentDto } from './../../../shared/interfaces/ride-firestore';
import { UserRole } from '../../../core/auth/enums/userRoles';
import { StateService } from '../../../shared/services/state.service';

@Injectable({
  providedIn: 'root',
})
export class MapRideServiceService {
  private readonly stateService = inject(StateService);

  statusSeverity: StatusSeverity = 'info'; //default
  TimezoneDropdownValue = signal<string>('');
  isClient = localStorage.getItem('role')?.toLowerCase() == UserRole.Client;
  isAdmin = localStorage.getItem('role')?.toLowerCase() == UserRole.Admin;

  // Live tracking statuses for admin users
  private readonly LiveTrackingStatuses = [RideStatusType.InProgress, RideStatusType.Completed, RideStatusType.NoShow, RideStatusType.CanceledLate, RideStatusType.CanceledInAdvance];

  getMappedItem(ride: RideDocumentDto): RideListItem {
    return {
      id: ride.original_ride_id,
      dispatchId: ride.id,
      uuid: ride.uuid,
      blueprintId: ride.ride_blueprint_id!,
      rideInfo: this.getRideInfo(ride),
      rideStatus: this.getRideStatus(ride),
      studentNames: this.getStudentNames(ride),
      cancellationReasonDetails: this.getCancellationReasonDetails(ride),
      attentionTooltip: this.getRideAttentionFlag(this.getRideJobStatus(ride)!, ride.date_of_service, ride.ride_times?.scheduled_first_Arrival_time_utc, ride.ride_status_id, ride.notified_applied),
      rideStatusSeverity: this.statusSeverity,
      rideTime: this.getRideTime(ride),
      rideTimeline: this.getRideStops(ride),
      driverDetails: this.getDriverDetails(ride),
      monitorDetails: this.getMonitorDetails(ride),
      clientDetails: this.getClientDetails(ride),
      students: this.getRideStudents(ride),
      jobStatus: RideStatusDescription(this.getRideJobStatus(ride)!),
      rideStatusId: ride.ride_status_id!,
      hasNote: this.isRideWithNote(ride),
      JobStatusId: ride.job_status_id!,
      rideAttentionStatusId: ride.attention_status_id!,
      isNeedMonitor: ride.ride_cargoes!.some(b => !b.is_deleted && b.is_attendant),
      numOfPickedUpStudents: this.getNumOfPickedUpStudents(ride),
      equipmentsNeed: this.getEquipmentNeeds(ride),
      equipmentAssignmentResult: this.checkEquipmentAssignment(ride),
      enableLiveTracking: this.getEnableLiveTracking(ride),
      isCameraRequired: this.getCameraStatus(ride),
      timezoneOffsetInSeconds: ride.ride_locations?.timezone_offset_in_seconds ?? 0,
    };
  }

  public getNumOfPickedUpStudents(ride?: RideDocumentDto): number {
    if (!ride) return 0;
    if (ride.ride_stops && ride.ride_stops?.length > 0) {
      const allCargoes: RideStopCargoDocumentDto[] = ride.ride_stops.flatMap(stop => stop.ride_stop_cargoes?.filter((cargo): cargo is RideStopCargoDocumentDto => cargo !== null) ?? []);

      const pickupCount = allCargoes.filter(cargo => cargo.action_type?.toLowerCase() == 'pick-up' && cargo.ride_status_id === rideStopCargoStatus.PickedUp).length;

      const dropoffCount = allCargoes.filter(cargo => cargo.action_type?.toLowerCase() == 'drop-off' && cargo.ride_status_id === rideStopCargoStatus.DroppedOff).length;

      return pickupCount - dropoffCount;
    }
    return 0;
  }

  private checkEquipmentAssignment(ride: RideDocumentDto) {
    const driverEquipmentIds: number[] = JSON.parse(ride.ride_drivers?.find(d => !d.is_deleted)?.ride_driver_extra_fields?.find(f => f.key_id == RideDriverExtraFields.EquipmentIds)?.value ?? '[]') ?? [];
    const rideRequiredEquipments: RidePassengerEquipmentDocumentDto[] = ride.ride_passenger_equipments ?? [];

    // Count required equipment quantities
    const requiredEquipmentMap = new Map<number, { equipment: RidePassengerEquipmentDocumentDto; requiredQuantity: number }>();

    rideRequiredEquipments.forEach(equipment => {
      const existing = requiredEquipmentMap.get(equipment.equipment_id);
      if (existing) {
        existing.requiredQuantity++;
      } else {
        requiredEquipmentMap.set(equipment.equipment_id, {
          equipment,
          requiredQuantity: 1,
        });
      }
    });

    // Count available equipment quantities from driver
    const availableEquipmentMap = new Map<number, number>();
    driverEquipmentIds.forEach(equipmentId => {
      const existing = availableEquipmentMap.get(equipmentId);
      availableEquipmentMap.set(equipmentId, (existing ?? 0) + 1);
    });

    const requiredAndAssigned: { equipment: RidePassengerEquipmentDocumentDto; quantity: number }[] = [];
    const requiredButNotAssigned: { equipment: RidePassengerEquipmentDocumentDto; quantity: number }[] = [];

    // Process each required equipment type
    requiredEquipmentMap.forEach((value, equipmentId) => {
      const { equipment, requiredQuantity } = value;

      const availableQuantity = availableEquipmentMap.get(equipmentId) ?? 0;

      const assignedQuantity = Math.min(requiredQuantity, availableQuantity);
      const notAssignedQuantity = requiredQuantity - assignedQuantity;

      // Add to assigned if any are available
      if (assignedQuantity > 0) {
        requiredAndAssigned.push({
          equipment: equipment,
          quantity: assignedQuantity,
        });
      }

      // Add to not assigned if any are missing
      if (notAssignedQuantity > 0) {
        requiredButNotAssigned.push({
          equipment: equipment,
          quantity: notAssignedQuantity,
        });
      }
    });

    const requiredAndAssignedTotalCount = requiredAndAssigned.reduce((sum, item) => sum + item.quantity, 0);
    const requiredButNotAssignedTotalCount = requiredButNotAssigned.reduce((sum, item) => sum + item.quantity, 0);

    return {
      requiredAndAssigned: requiredAndAssigned.length > 0 ? { total: requiredAndAssignedTotalCount, equipments: requiredAndAssigned } : null,
      requiredButNotAssigned: requiredButNotAssigned.length > 0 ? { total: requiredButNotAssignedTotalCount, equipments: requiredButNotAssigned } : null,
    };
  }

  private getEquipmentNeeds(ride: RideDocumentDto): { equipmentsNeed: EquipmentsNeed; description: string } {
    const equipmentsNeed = ride.ride_extra_fields?.find(f => f.key_id == RidesExtraFields.EquipmentsNeed)?.value as unknown as EquipmentsNeed;
    const description = getEquipmentsNeedDescription(equipmentsNeed);
    return { equipmentsNeed, description };
  }

  private isRideWithNote(ride: RideDocumentDto): boolean {
    if (ride.ride_extra_fields?.find(f => f.key_id == RidesExtraFields.IsHasInternalNote)?.value?.toLowerCase() == 'true') {
      return true;
    }
    return false;
  }

  formatPhoneNumber(phone: string): string {
    const cleaned = phone.replaceAll(/\D/g, ''); // Remove non-digit characters
    if (cleaned.length === 10) {
      return cleaned.replace(/^(\d{3})(\d{3})(\d{4})$/, '($1) $2-$3');
    }
    return '';
  }

  getStudentNames(ride: RideDocumentDto) {
    let studentNames: string[] = [];
    ride.ride_cargoes?.forEach(c => {
      if (!c.is_deleted && c.student_id) {
        const firstName = c.ride_cargo_extra_fields?.find(f => f.key_id == RideCargoExtraFields.FirstName)?.value ?? '';
        const lastName = c.ride_cargo_extra_fields?.find(f => f.key_id == RideCargoExtraFields.LastName)?.value ?? '';
        studentNames.push(`${firstName} ${lastName}`);
      }
    });
    return studentNames;
  }

  private maskNumber(value: number | string): string {
    const strValue = value.toString();
    const length = strValue.length;
    return 'x'.repeat(length - 4) + strValue.slice(-4);
  }

  getMappedContractor(driverLookup: DriverLookup): IContractorDialog {
    const signInStatus = driverLookup.isSignedOut ? 'No ' : 'Yes ';
    const signInStatusDate = driverLookup.isSignedOut ? '(' + moment.utc(driverLookup.signedOutAtUtc).local().format('MM/DD/YYYY hh:mm A') + ')' : '(' + moment.utc(driverLookup.createdAtUtc).local().format('MM/DD/YYYY hh:mm A') + ')';

    const basicInfo = {
      driverProvider: driverLookup.organization,
      mobileNumber: this.formatPhoneNumber(driverLookup.mobileNumber),
      email: driverLookup.email,
      gender: driverLookup.gender,
      dateOfBirth: driverLookup.dateOfBirth,
      joinedSince: driverLookup.joinDate,
      primaryLanguage: getLanguage(driverLookup.primaryLanguage ?? ''),
      address: driverLookup.address,
    } as ContractorBasicInfo;
    const bankInfo = [
      { name: 'Bank Name', value: driverLookup.bankName },
      { name: 'Holder Name', value: driverLookup.holderName },
      { name: 'Account Number', value: this.maskNumber(driverLookup.accountNumber ?? '') },
      { name: 'Routing Number', value: this.maskNumber(driverLookup.routingNumber ?? '') },
      { name: 'Account Type', value: driverLookup.accountType },
      {
        name: 'Status',
        value: driverLookup.isActive ? 'Verified' : 'Not Verified',
        isTag: true,
        tagSeverity: driverLookup.isActive ? 'success' : 'danger',
      },
    ] as InfoCardItem[];
    const vehicleInfo = [
      { name: 'Year', value: driverLookup.vehicles?.year },
      { name: 'Color', value: driverLookup.vehicles?.color },
      { name: 'Manufacturer', value: driverLookup.vehicles?.make },
      { name: 'Model', value: driverLookup.vehicles?.model },
      { name: 'Plate Number', value: driverLookup.vehicles?.plateNumber },
      { name: 'Class', value: driverLookup.vehicles?.classification },
      { name: 'Service Area', value: driverLookup.serviceArea },
    ] as InfoCardItem[];
    const deviceBasicInfo = [
      { name: 'OS Type', value: driverLookup.osType },
      { name: 'OS Version', value: driverLookup.osVersion },
      { name: 'Device Model', value: driverLookup.deviceModel },
    ] as InfoCardItem[];
    const adroitAppInfo = [
      {
        name: 'ADROIT App Version Installed',
        value: driverLookup.adroitAppVersion,
      },
      { name: 'Sign In Status', value: { status: signInStatus, date: signInStatusDate } },
      {
        name: 'Notifications ',
        value: driverLookup.notificationsEnabled ? 'Enabled' : 'Disabled',
        isTag: true,
        tagSeverity: driverLookup.notificationsEnabled ? 'success' : 'secondary',
      },
      {
        name: 'Location Services ',
        value: driverLookup.locationServiceEnabled ? 'Enabled' : 'Disabled',
        isTag: true,
        tagSeverity: driverLookup.locationServiceEnabled ? 'success' : 'secondary',
      },
      {
        name: 'Location Services Setting',
        value: driverLookup.locationServicSetting,
      },
      {
        name: 'Location Services (Full Accuracy)',
        value: driverLookup.locationServicFullAccuracy ? 'Enabled' : 'Disabled',
        isTag: true,
        tagSeverity: driverLookup.locationServicFullAccuracy ? 'success' : 'secondary',
      },
      {
        name: 'Power Savings Mode',
        value: driverLookup.powerSavingMode ? 'Enabled' : 'Disabled',
        isTag: true,
        tagSeverity: driverLookup.powerSavingMode ? 'success' : 'secondary',
      },
    ] as InfoCardItem[];
    let contactNames = driverLookup.providerContactName?.trim().slice(0, -1).split(',') ?? [];
    let contactTypes = driverLookup.providerContactType?.trim().slice(0, -1).split(',') ?? [];
    let contactReachAt = driverLookup.providerContactReachAt?.trim().slice(0, -1).split(',') ?? [];
    let driverProviderContacts: ContractorDriverProviderContact[] = [];
    let index = 0;
    contactNames.forEach(element => {
      driverProviderContacts.push({
        name: element,
        type: contactTypes[index],
        reachAt: this.formatPhoneNumber(contactReachAt[index]),
      });
      index += 1;
    });
    return {
      basicInformation: basicInfo,
      bankInfo: bankInfo,
      vehicleInfo: driverLookup.isDriver ? vehicleInfo : null,
      deviceBasicInfo: deviceBasicInfo,
      adroitAppInfo: adroitAppInfo,
      driverProviderContacts: driverProviderContacts,
    } as IContractorDialog;
  }

  private formatMinutes(totalMinutes: number, fullDate = false): string {
    const minutesPerHour = 60;
    const minutesPerDay = 24 * minutesPerHour;

    const days = Math.floor(Math.abs(totalMinutes) / minutesPerDay);
    const remainingMinutes = Math.abs(totalMinutes) % minutesPerDay;

    const hours = Math.floor(remainingMinutes / minutesPerHour);
    const minutes = Math.floor(remainingMinutes % minutesPerHour);

    const formattedTime: string[] = [];
    if (days > 0) {
      formattedTime.push(`${days}${fullDate ? ' days' : 'd'}`);
    }
    if (hours > 0) {
      formattedTime.push(`${hours}${fullDate ? ' hours' : 'h'}`);
    }
    if (minutes > 0 || (days === 0 && hours === 0)) {
      formattedTime.push(`${minutes}${fullDate ? ' minutes' : 'm'}`);
    }

    return formattedTime.join(' ');
  }

  private getRideStops(ride: RideDocumentDto) {
    const scheduleTimeAdjustments = this.parseScheduleTimeAdjustments(ride);
    return ride.ride_stops!.map(stop => this.processRideStop(stop, ride, scheduleTimeAdjustments));
  }

  private parseScheduleTimeAdjustments(ride: RideDocumentDto): ScheduleTimeAdjustmentsDocumentDto[] | undefined {
    const scheduleVal = this.rideExtraFieldReader(RidesExtraFields.ScheduleTimeAdjustments, ride);
    return scheduleVal ? (JSON.parse(scheduleVal) as ScheduleTimeAdjustmentsDocumentDto[]) : undefined;
  }

  private processRideStop(stop: RideStopDocumentDto, ride: RideDocumentDto, scheduleTimeAdjustments?: ScheduleTimeAdjustmentsDocumentDto[]): IStep {
    const statusInfo = this.determineStopStatus(stop);
    const timeInfo = this.calculateStopTimes(stop, ride, statusInfo.statusType);

    return {
      time: timeInfo.displayTime,
      active: statusInfo.isActive,
      color: statusInfo.color,
      attentionColor: timeInfo.color ?? statusInfo.color,
      statusType: statusInfo.statusType,
      statusId: stop.status_id ?? 0,
      stopId: stop.id ?? 0,
      arrivalTimeStatus: timeInfo.arrivalStatus,
      statusDescription: timeInfo.statusDescription,
      scheduledArrivalTime: timeInfo.scheduledArrivalTime, // updates based on the timezone DDL
      scheduled_arrival_time: stop.ride_stop_times?.scheduled_arrival_time ?? '00:00:00', // always in ride's local time
      scheduled_arrival_time_utc: stop.ride_stop_times?.scheduled_arrival_time_utc ?? '00:00:00', // UTC time
      estimatedArrivalTime: timeInfo.estimatedArrivalTime,
      actualArrivalTime: timeInfo.actualArrivalTime,
      actualArrivalTimeUtc: timeInfo.actualArrivalTimeUtc,
      weeklyAdjustments: this.getWeeklyAdjustment(scheduleTimeAdjustments, stop.ride_stop_times!.scheduled_arrival_time ?? '0'),
      instructions: stop.instructions?.map((b: any) => b.description ?? ''),
      place: this.rideStopExtraFieldReader(RideStopExtraFields.Name, stop.ride_stop_extra_fields!),
      placeType: this.rideStopExtraFieldReader(RideStopExtraFields.Type, stop.ride_stop_extra_fields!),
      address: this.formatStopAddress(stop),
      cargoes: this.getStopPassenger(stop.ride_stop_cargoes!, ride.ride_cargoes!),
      stopLocation: { longitude: stop.ride_stop_locations?.longitude, latitude: stop.ride_stop_locations?.latitude },
      originalRideId: ride.original_ride_id,
      delayReason: ride.ride_telemetry?.delay_reason?.[stop.id]?.slice(-1)[0] ?? 'N/A',
    };
  }

  formatStopAddress(stop: any): string {
    return `${this.rideStopExtraFieldReader(RideStopExtraFields.Street1, stop.ride_stop_extra_fields)} ${this.rideStopExtraFieldReader(RideStopExtraFields.City, stop.ride_stop_extra_fields)} ${this.rideStopExtraFieldReader(RideStopExtraFields.State, stop.ride_stop_extra_fields)} ${this.rideStopExtraFieldReader(RideStopExtraFields.Zipcode, stop.ride_stop_extra_fields)}`;
  }

  determineStopStatus(stop: any): {
    statusType: RideStopStatusType;
    color: badgeSeverity;
    isActive: boolean;
  } {
    let statusType: RideStopStatusType = 'None';
    let color: badgeSeverity = 'secondary';
    let isActive = false;

    switch (stop.status_id) {
      case RideStopStatus.EnRoute:
        color = 'warning';
        statusType = 'EnRoute';
        isActive = true;
        break;
      case RideStopStatus.Arrived:
        color = 'info';
        statusType = 'Arrived';
        isActive = true;
        break;
      case RideStopStatus.Skipped:
        statusType = 'Skipped';
        break;
      case RideStopStatus.Visited:
        color = 'success';
        statusType = 'Visited';
        break;
    }

    return { statusType, color, isActive };
  }

  calculateStopTimes(
    stop: any,
    ride: RideDocumentDto,
    statusType: RideStopStatusType,
    _12HourFormat: boolean = true,
  ): {
    displayTime: string;
    scheduledArrivalTime: string;
    estimatedArrivalTime: string;
    actualArrivalTime: string;
    color?: badgeSeverity;
    arrivalStatus: StopArrivalTimeStatus;
    statusDescription: string;
    actualArrivalTimeUtc: string;
  } {
    // Default values
    let displayTime = this.changeTime(stop.ride_stop_times!.scheduled_arrival_time_utc ?? '0', ride, _12HourFormat);
    let scheduledArrivalTime = this.changeTime(stop.ride_stop_times!.scheduled_arrival_time_utc ?? '0', ride, _12HourFormat);
    let estimatedArrivalTime = '';
    let actualArrivalTime = '';
    let color: badgeSeverity | undefined = undefined;
    let arrivalStatus: StopArrivalTimeStatus = 'none';
    let statusDescription = '';
    let actualArrivalTimeUtc = stop.ride_stop_times!.actual_arrival_time_utc;

    if (!this.isActiveRideState(ride.ride_status_id ?? 0)) {
      return { displayTime, scheduledArrivalTime, estimatedArrivalTime, actualArrivalTime, arrivalStatus, statusDescription, actualArrivalTimeUtc };
    }

    if (statusType === 'EnRoute') {
      return this.calculateEnRouteTimings(stop, ride, scheduledArrivalTime, _12HourFormat, actualArrivalTimeUtc);
    } else if (statusType === 'Arrived' || statusType === 'Visited') {
      return this.calculateArrivedTimings(stop, ride, scheduledArrivalTime, _12HourFormat, actualArrivalTimeUtc);
    } else if (stop.status_id === RideStopStatus.None) {
      return this.calculateNoneStatusTimings(stop, ride, scheduledArrivalTime, _12HourFormat);
    } else if (stop.status_id === RideStopStatus.Skipped && ride.ride_status_id !== RideStatusType.CanceledDueToHoliday) {
      return {
        displayTime,
        scheduledArrivalTime,
        estimatedArrivalTime,
        actualArrivalTime,
        actualArrivalTimeUtc,
        arrivalStatus: 'skipped',
        statusDescription: 'Skipped',
      };
    }

    return { displayTime, scheduledArrivalTime, estimatedArrivalTime, actualArrivalTime, arrivalStatus, statusDescription, actualArrivalTimeUtc };
  }

  private isActiveRideState(rideStatusId: number): boolean {
    return [RideStatusType.InProgress, RideStatusType.Completed, RideStatusType.CanceledDueToHoliday, RideStatusType.CanceledInAdvance, RideStatusType.CanceledLate, RideStatusType.NoShow].includes(rideStatusId);
  }

  private calculateEnRouteTimings(stop: any, ride: RideDocumentDto, scheduledArrivalTime: string, _12HourFormat: boolean, actualArrivalTimeUtc?: string): any {
    let arrivalStatus: StopArrivalTimeStatus = 'none';
    let statusDescription = '';
    let estimatedArrivalTime = '';
    let actualArrivalTime = null;
    let displayTime = scheduledArrivalTime;

    const telemetryEta = ride.ride_telemetry?.eta?.[stop.id];
    const lastEstimatedArrivalTimeUtc = telemetryEta?.last_estimated_arrival_time_utc;
    const scheduledAndLastEstimatedDiffInSeconds = telemetryEta?.scheduled_and_last_estimated_arrival_time_difference_in_seconds;

    if (lastEstimatedArrivalTimeUtc && lastEstimatedArrivalTimeUtc !== '0' && lastEstimatedArrivalTimeUtc !== '00:00:00' && lastEstimatedArrivalTimeUtc !== '') {
      const diff = scheduledAndLastEstimatedDiffInSeconds === 0 ? 0 : Math.round((scheduledAndLastEstimatedDiffInSeconds ?? 0) / 60) * -1;

      estimatedArrivalTime = this.changeTime(lastEstimatedArrivalTimeUtc, ride, _12HourFormat);
      displayTime = scheduledArrivalTime;

      if (diff === 0) {
        statusDescription = actualArrivalTime ?? '';
        arrivalStatus = 'onTime';
      } else if (diff > 0) {
        statusDescription = `+ ${this.formatMinutes(diff)}`;
        arrivalStatus = 'late';
      } else {
        statusDescription = `- ${this.formatMinutes(diff)}`;
        arrivalStatus = 'early';
      }
    } else {
      estimatedArrivalTime = scheduledArrivalTime;
    }

    return {
      displayTime,
      scheduledArrivalTime,
      estimatedArrivalTime,
      actualArrivalTime,
      actualArrivalTimeUtc,
      arrivalStatus,
      statusDescription,
    };
  }

  private calculateArrivedTimings(stop: any, ride: RideDocumentDto, scheduledArrivalTime: string, _12HourFormat: boolean, actualArrivalTimeUtc?: string): any {
    const dateOfService = this.getDateOfService(ride.date_of_service);

    const scheduledAndActualArrivalTimeDifferenceInSeconds = this.getDifferenceTimeInSeconds(stop.ride_stop_times!.actual_arrival_time_utc, stop.ride_stop_times!.scheduled_arrival_time_utc, dateOfService);

    let color: badgeSeverity | undefined = undefined;
    if (scheduledAndActualArrivalTimeDifferenceInSeconds > 59) {
      color = 'danger';
    } else if (scheduledAndActualArrivalTimeDifferenceInSeconds < -59) {
      color = 'success';
    }

    const diff = !Number.isNaN(scheduledAndActualArrivalTimeDifferenceInSeconds) && scheduledAndActualArrivalTimeDifferenceInSeconds === 0 ? 0 : Math.round(scheduledAndActualArrivalTimeDifferenceInSeconds / 60);

    const actualArrivalTime =
      stop.ride_stop_times!.actual_arrival_time_utc && stop.ride_stop_times!.actual_arrival_time_utc !== '00:00:00' && stop.ride_stop_times!.actual_arrival_time_utc > '0'
        ? this.changeTime(stop.ride_stop_times!.actual_arrival_time_utc, ride, _12HourFormat)
        : this.changeTime(stop.ride_stop_times!.scheduled_arrival_time_utc ?? '0', ride, _12HourFormat);

    let arrivalStatus: StopArrivalTimeStatus;
    let statusDescription = '';

    if (diff === 0) {
      arrivalStatus = 'onTime';
    } else if (diff > 0) {
      statusDescription = `+ ${this.formatMinutes(diff)}`;
      arrivalStatus = 'late';
    } else {
      statusDescription = `- ${this.formatMinutes(diff)}`;
      arrivalStatus = 'early';
    }

    let estimatedArrivalTime = null;
    const telemetryEta = ride.ride_telemetry?.eta?.[stop.id];
    const lastEstimatedArrivalTimeUtc = telemetryEta?.last_estimated_arrival_time_utc;
    if (lastEstimatedArrivalTimeUtc && lastEstimatedArrivalTimeUtc !== '0' && lastEstimatedArrivalTimeUtc !== '00:00:00' && lastEstimatedArrivalTimeUtc !== '') {
      estimatedArrivalTime = this.changeTime(lastEstimatedArrivalTimeUtc, ride, _12HourFormat);
    }

    return {
      displayTime: scheduledArrivalTime,
      scheduledArrivalTime,
      estimatedArrivalTime: estimatedArrivalTime ?? scheduledArrivalTime,
      actualArrivalTime,
      actualArrivalTimeUtc,
      color,
      arrivalStatus,
      statusDescription,
    };
  }

  private calculateNoneStatusTimings(stop: any, ride: RideDocumentDto, scheduledArrivalTime: string, _12HourFormat: boolean): any {
    let estimatedArrivalTime;

    const telemetryEta = ride.ride_telemetry?.eta?.[stop.id];
    const lastEstimatedArrivalTimeUtc = telemetryEta?.last_estimated_arrival_time_utc ?? '0';

    if (lastEstimatedArrivalTimeUtc !== '0' && lastEstimatedArrivalTimeUtc !== '00:00:00' && lastEstimatedArrivalTimeUtc !== '') {
      estimatedArrivalTime = this.changeTime(lastEstimatedArrivalTimeUtc, ride, _12HourFormat);
    } else {
      estimatedArrivalTime = scheduledArrivalTime;
    }

    return {
      displayTime: scheduledArrivalTime,
      scheduledArrivalTime,
      estimatedArrivalTime,
      actualArrivalTime: null,
      arrivalStatus: 'none' as StopArrivalTimeStatus,
      statusDescription: '',
    };
  }

  getWeeklyAdjustment(scheduleTimeAdjustments: ScheduleTimeAdjustmentsDocumentDto[] | undefined, ScheduledArrivalTime: string) {
    let schedule = '';

    if (scheduleTimeAdjustments) {
      scheduleTimeAdjustments.forEach(item => {
        let day = 'every Sunday';
        if (item.RecurrenceDay == RecurrenceDay.everyMonday) day = 'every Monday';
        else if (item.RecurrenceDay == RecurrenceDay.everyTuesday) day = 'every Tuesday';
        else if (item.RecurrenceDay == RecurrenceDay.everyWednesday) day = 'every Wednesday';
        else if (item.RecurrenceDay == RecurrenceDay.everyThursday) day = 'every Thursday';
        else if (item.RecurrenceDay == RecurrenceDay.everyFriday) day = 'every Friday';
        else if (item.RecurrenceDay == RecurrenceDay.everySaturday) day = 'every Saturday';

        const formattedTime = moment(ScheduledArrivalTime).add(item.AdjustedDurationInMinutes, 'minutes').format('hh:mm A');
        schedule = `${item.TypeName} ${day} at' ${formattedTime}`;
      });
      return schedule;
    }
    return '';
  }

  getRideTime(ride: RideDocumentDto, _12HourFormat: boolean = true) {
    //this is local tz
    //for utcTimezoneDiff will get difference time between local and utc
    const _serverTimezone = localStorage.getItem('serverTimezone') ?? '';

    let utcTimezoneDiff = '';
    let utcTimezoneDiffBPFormat = '';
    if (this.TimezoneDropdownValue() == 'systemPstTimezone') {
      utcTimezoneDiff = `(${_serverTimezone}) UTC`;
      utcTimezoneDiffBPFormat = `(UTC-${_serverTimezone[2]})`;
    } else if (this.TimezoneDropdownValue() == 'RideTimezone') {
      //ride time regardless of TZ
      const strOffset = ride.ride_locations?.timezone_offset_in_seconds! / 60 / 60;
      utcTimezoneDiff = strOffset > 0 ? `(+${strOffset}:00) UTC` : `(${strOffset}:00) UTC`;
      utcTimezoneDiffBPFormat = `(UTC${strOffset}:00)`;
    } else {
      const timeDifference = moment().utcOffset();
      const offset = timeDifference / 60;
      const strOffset = timeDifference > 0 ? `+${offset}:00` : `${offset}:00`;

      utcTimezoneDiff = `(${strOffset}) UTC`;
      utcTimezoneDiffBPFormat = `(UTC${strOffset})`;
    }
    const obj = {
      from: this.changeTime(ride.ride_times?.scheduled_first_Arrival_time_utc ?? '0', ride, _12HourFormat),
      to: this.changeTime(ride.ride_times?.scheduled_last_Arrival_time_utc ?? '0', ride, _12HourFormat),
      utcTimezoneDiff: utcTimezoneDiff,
      utcTimezoneDiffBPFormat: utcTimezoneDiffBPFormat,
    } as RideTime;
    return obj;
  }

  private changeTime(strTime: string, ride: RideDocumentDto, _12HourFormat: boolean = true) {
    //strTime always UTC
    const dateOfService = typeof ride.date_of_service == 'string' ? moment(ride.date_of_service).format('MM-DD-YYYY') : moment.unix((ride.date_of_service as Timestamp).seconds).format('MM-DD-YYYY');
    let timeToReturn;
    //this.TimezoneDropdownValue  //DeviceTimezone || systemPstTimezone || RideTimezone
    const _serverTimezone = localStorage.getItem('serverTimezone') ?? '';
    if (this.TimezoneDropdownValue() == 'systemPstTimezone') {
      timeToReturn = moment
        .utc((dateOfService + ' ' + strTime).replaceAll('-', '/'))
        .zone(_serverTimezone)
        .format(_12HourFormat ? 'hh:mm A' : 'HH:mm');
    } else if (this.TimezoneDropdownValue() == 'RideTimezone') {
      let strOffset = '';
      const timeOffset = ride.ride_locations?.timezone_offset_in_seconds! / 60 / 60;
      if (timeOffset < 0) {
        strOffset = `-0${timeOffset * -1}:00`;
      } else {
        strOffset = `0${timeOffset * -1}:00`;
      }
      timeToReturn = moment
        .utc((dateOfService + ' ' + strTime).replaceAll('-', '/'))
        .zone(strOffset)
        .format(_12HourFormat ? 'hh:mm A' : 'HH:mm');
    } else {
      //device local TZ
      timeToReturn = moment
        .utc((dateOfService + ' ' + strTime).replaceAll('-', '/'))
        .local()
        .format(_12HourFormat ? 'hh:mm A' : 'HH:mm');
    }
    return timeToReturn;
  }

  private getStopPassenger(cargoes: RideStopCargoDocumentDto[], rideCargoes: RideCargoDocumentDto[]) {
    const obj = [] as IStepCargoes[];
    cargoes.forEach(cargo => {
      const rideCargo = rideCargoes.find(b => b.original_ride_cargo_id == cargo.ride_cargo_id); //it's the original rideCargo id
      if (rideCargo) {
        obj.push({
          stopCargoId: cargo.id,
          actionType: cargo.action_type ?? '',
          name: `${this.rideCargoExtraFieldReader(RideCargoExtraFields.FirstName, rideCargo?.ride_cargo_extra_fields!)} ${this.rideCargoExtraFieldReader(RideCargoExtraFields.LastName, rideCargo?.ride_cargo_extra_fields!)}`,
          type:
            this.rideCargoExtraFieldReader(RideCargoExtraFields.PassengerType, rideCargo?.ride_cargo_extra_fields!).toLowerCase() === 'student'
              ? this.rideCargoExtraFieldReader(RideCargoExtraFields.PassengerType, rideCargo?.ride_cargo_extra_fields!)
              : 'Monitor',
          status: this.getCargoStopStatus(cargo.ride_status_id!),
          statusId: cargo.ride_status_id ?? 0,
          rideCargoId: cargo.ride_cargo_id ?? 0,
          isTransportationConfirmed: this.rideCargoExtraFieldReader(RideCargoExtraFields.IsTransportationConfirmed, rideCargo?.ride_cargo_extra_fields!).toLowerCase() == 'true',
        });
      }
    });
    return obj;
  }
  private getCargoStopStatus(cargoStatusId: number) {
    let status = '';
    if (cargoStatusId == RideStatusType.NoShow || cargoStatusId == RideStatusType.CanceledInAdvance || cargoStatusId == RideStatusType.CanceledLate || cargoStatusId == RideStatusType.CanceledDueToHoliday) {
      status = RideStatusDescription(cargoStatusId);
    } else if (
      cargoStatusId == rideStopCargoStatus.PickedUp ||
      cargoStatusId == rideStopCargoStatus.DroppedOff ||
      cargoStatusId == rideStopCargoStatus.Absent ||
      cargoStatusId == rideStopCargoStatus.NotDroppedOff ||
      cargoStatusId == rideStopCargoStatus.SkipDropOff
    ) {
      status = StopCargoStatusDescription(cargoStatusId);
    }
    return status;
  }

  getDateOfService(dateOfService: any) {
    return typeof dateOfService == 'string' ? moment(dateOfService).format('MM-DD-YYYY') : moment.unix((dateOfService as Timestamp).seconds).format('MM-DD-YYYY');
  }

  getDifferenceTimeInSeconds(time1: string | null, time2: string | null, dateOfService: string) {
    if (time1 && time1 != '0' && time1 != '00:00:00' && time2 && time2 != '0' && time2 != '00:00:00') {
      const diff = moment(dateOfService + ' ' + time1).diff(moment(dateOfService + ' ' + time2), 'seconds');
      return diff;
    } else return 0;
  }
  private getRideInfo(ride: RideDocumentDto) {
    const obj = {
      type: this.rideExtraFieldReader(RidesExtraFields.Type, ride),
      dateOfService: ride.date_of_service,
      strDateOfService: ride.index_date_of_service,
      tag: this.rideExtraFieldReader(RidesExtraFields.IsTest, ride)?.toLowerCase() === 'true' ? 'Test' : '',
      period: this.rideExtraFieldReader(RidesExtraFields.Period, ride),
      milage: ride.estimated_distance_in_miles,
      duration: ride.ride_times?.estimated_duration_in_minutes ?? 0,
      numOfStudents: Number(this.rideExtraFieldReader(RidesExtraFields.NumberOfStudents, ride)),
      numOfMonitors: Number(this.rideExtraFieldReader(RidesExtraFields.NumberOfAssistants, ride)) + Number(this.rideExtraFieldReader(RidesExtraFields.NumberOfAides, ride)),
      TotalBillableWaitingTimeInMinutes: ride.ride_times?.total_billable_waiting_time_in_minutes,
    } as RideInfo;
    return obj;
  }

  getCancellationReasonDetails(ride: RideDocumentDto): { message: string; reasonId: number; reasonName: string } | null {
    const cancellationReasonId = ride.ride_extra_fields?.find(f => f.key_id == RidesExtraFields.CancellationReasons)?.value;
    if (!cancellationReasonId) {
      return null;
    }
    const modificationTime = ride.modified_at_utc;
    const cancellationReasonNote = ride.ride_extra_fields?.find(f => f.key_id == RidesExtraFields.CancellationReasonNote)?.value ?? null;
    const cancellationReason = this.stateService.getCancellationReasonsLookup().find(cr => cr.id === Number(cancellationReasonId));

    // Format the timestamp
    let formattedTime = '';
    if (modificationTime && typeof modificationTime === 'object' && 'seconds' in modificationTime) {
      // It's a Firestore Timestamp
      formattedTime = moment.unix((modificationTime as Timestamp).seconds).format('MMM DD, YYYY, hh:mm A');
    } else if (typeof modificationTime === 'string') {
      // It's a string date
      formattedTime = moment(modificationTime).format('MMM DD, YYYY, hh:mm A');
    }

    const message = `Cancelled due to ${cancellationReasonNote?.trim() === '' ? cancellationReason?.name : cancellationReasonNote} - ${formattedTime} by ${ride.modified_by ?? 'admin'}`;

    return {
      message,
      reasonId: Number(cancellationReasonId),
      reasonName: cancellationReason?.name ?? 'Other',
    };
  }

  getRideStatus(ride: RideDocumentDto) {
    let status = 'Schedule';

    //step 1
    let rideJobStatus = this.getRideJobStatus(ride);
    let showRideAttention = this.getRideAttentionFlag(rideJobStatus!, ride.date_of_service, ride.ride_times?.scheduled_first_Arrival_time_utc, ride.ride_status_id, ride.notified_applied).hasAttention;
    //step 2
    if (ride.attention_status_id && ride.attention_status_id > 0 && ride.attention_status_id != RideAttentionStatus.none && rideJobStatus != RideStatusType.Declined) {
      status = RideAttentionStatusDescription(ride.attention_status_id, rideJobStatus!, this.isClient);
    } else {
      status = RideStatusDescription(ride.ride_status_id!);
      if (status == 'none') {
        if (rideJobStatus == RideStatusType.Accepted) {
          status = RideStatusDescription(RideStatusType.Accepted);
        } else if (rideJobStatus == RideStatusType.Offered) {
          status = RideStatusDescription(RideStatusType.Offered);
        } else if (rideJobStatus == RideStatusType.Declined) {
          status = RideStatusDescription(RideStatusType.Declined);
        } else {
          status = 'Scheduled';
        }
      }
    }
    //step 3
    this.statusSeverity = GetRideStatusSeverity(status, showRideAttention);
    return status;
  }

  public getRideAttentionFlag(rideJobStatus: number, date_of_service: string, scheduled_first_Arrival_time_utc: string | null | undefined, ride_status_id: number | null, notified_applied: number | null) {
    let rideAttentionTooltipDescription = '';

    let currentTime = moment().utc().format('HH:mm:ss');
    const dateOfService = this.getDateOfService(date_of_service);

    let diffInSec = scheduled_first_Arrival_time_utc ? this.getDifferenceTimeInSeconds(scheduled_first_Arrival_time_utc, currentTime, dateOfService) : 0;
    const minus = diffInSec < 0 ? '-' : '';

    if (
      ride_status_id == RideStatusType.None &&
      rideJobStatus == RideStatusType.Offered &&
      ((notified_applied ?? 0) & AutomaticJobDispatcherNotificationType.Minutes45BeforeJobStart) == AutomaticJobDispatcherNotificationType.Minutes45BeforeJobStart
    ) {
      rideAttentionTooltipDescription = `This ride starts in ${minus}${this.formatMinutes(diffInSec / 60 + 1, true)}, and the driver has not accepted it yet.`;

      return { hasAttention: true, description: rideAttentionTooltipDescription };
    } else if ((rideJobStatus == RideStatusType.Accepted && (notified_applied ?? 0) & AutomaticJobDispatcherNotificationType.Minutes20AfterAcceptedJob) == AutomaticJobDispatcherNotificationType.Minutes20AfterAcceptedJob) {
      rideAttentionTooltipDescription = `The first pick-up is in ${minus}${this.formatMinutes(diffInSec / 60 + 1, true)}, and the driver has pressed the Start Ride button yet.`;
      return { hasAttention: true, description: rideAttentionTooltipDescription };
    }
    return { hasAttention: false, description: '' };
  }

  getDriverDetails(ride: RideDocumentDto) {
    const driver = ride.ride_drivers?.find(b => !b.is_deleted) as RideDriverDocumentDto;

    if (driver) {
      const firstName = driver.ride_driver_extra_fields?.find(b => b.key_id == RideDriverExtraFields.FirstName)?.value;
      const lastName = driver.ride_driver_extra_fields?.find(b => b.key_id == RideDriverExtraFields.LastName)?.value;
      const imageUrl = driver.ride_driver_extra_fields?.find(b => b.key_id == RideDriverExtraFields.ProfilePhotoCdnUrl)?.value;
      const blueprintCarType = this.rideExtraFieldReader(RidesExtraFields.VehicleTypeName, ride);
      const driverProvider = driver.ride_driver_extra_fields?.find(rd => rd.key_id == RideDriverExtraFields.OrganizationName)?.value;

      const plateNumber = driver.ride_driver_extra_fields?.find(b => b.key_id == RideDriverExtraFields.VehiclePlateNumber)?.value;
      const driverCarType = driver.ride_driver_extra_fields?.find(b => b.key_id == RideDriverExtraFields.Classification)?.value;
      const vehicleManufacturer = driver.ride_driver_extra_fields?.find(b => b.key_id == RideDriverExtraFields.VehicleMake)?.value;
      const vehicleModel = driver.ride_driver_extra_fields?.find(b => b.key_id == RideDriverExtraFields.VehicleModel)?.value;
      const vehicleColor = driver.ride_driver_extra_fields?.find(b => b.key_id == RideDriverExtraFields.VehicleColor)?.value;
      const vehicleYear = driver.ride_driver_extra_fields?.find(b => b.key_id == RideDriverExtraFields.Year)?.value;

      const obj = {
        name: `${firstName} ${lastName}`,
        img: imageUrl,
        online: false, //initial value
        type: 'Contractor',
        number: driver.driver_id!.toString(),
        blueprintCarType: blueprintCarType,
        driverProvider,
        id: driver.driver_id,
        plateNumber,
        driverCarType,
        vehicleManufacturer,
        vehicleModel,
        vehicleColor,
        vehicleYear,
      } as DriverDetails;
      return obj;
    }
    return null;
  }

  private getMonitorDetails(ride: RideDocumentDto) {
    const monitor = ride.ride_cargoes?.find(b => !b.is_deleted && b.is_attendant) as RideCargoDocumentDto;

    if (monitor) {
      const firstName = monitor.ride_cargo_extra_fields?.find(b => b.key_id == RideCargoExtraFields.FirstName)?.value;
      const lastName = monitor.ride_cargo_extra_fields?.find(b => b.key_id == RideCargoExtraFields.LastName)?.value;
      const imageUrl = monitor.ride_cargo_extra_fields?.find(b => b.key_id == RideCargoExtraFields.ProfilePhotoCdnUrl)?.value;

      const obj = {
        name: `${firstName} ${lastName}`,
        img: imageUrl,
        type: 'Monitor',
        number: monitor.assistant_id!.toString(),
        id: monitor.assistant_id,
      } as MonitorDetails;
      return obj;
    }
    return null;
  }

  private getRideStudents(ride: RideDocumentDto): RideStudent[] {
    const students = ride.ride_cargoes?.flatMap(cargo => {
      if (cargo.student_id != 0) {
        return {
          id: cargo.student_id ?? undefined,
          firstName: cargo.ride_cargo_extra_fields?.find(field => field.key_id == RideCargoExtraFields.FirstName)?.value,
          lastName: cargo.ride_cargo_extra_fields?.find(field => field.key_id == RideCargoExtraFields.LastName)?.value,
          payingClientId: cargo.paying_client_id ?? undefined,
          payingClientName: cargo.ride_cargo_extra_fields?.find(field => field.key_id == RideCargoExtraFields.PayingClientName)?.value,
        };
      }
      return [];
    });

    return students || [];
  }
  getCameraStatus(ride: RideDocumentDto): boolean {
    let isCameraRequired: boolean = false;
    ride.ride_cargoes?.forEach(c => {
      const currentStudentStopCargos = this.getStopCargosByCargoId(ride, c.original_ride_cargo_id);
      // check if the cargo's status id in any stop is early/late cancelled
      const isCancelled = currentStudentStopCargos.some(sc => sc.ride_status_id == rideStopCargoStatus.CanceledInAdvance || sc.ride_status_id == rideStopCargoStatus.CanceledLate || sc.ride_status_id == rideStopCargoStatus.CanceledDueToHoliday);

      if (!c.is_deleted && c.student_id && !isCancelled && !isCameraRequired) {
        isCameraRequired = c.ride_cargo_extra_fields?.find(f => f.key_id == RideCargoExtraFields.IsCameraRequired)?.value == 'true';
      }
    });
    return isCameraRequired;
  }

  getStopCargosByCargoId(ride: RideDocumentDto, cargoId: number): RideStopCargoDocumentDto[] {
    let stopCargoes: RideStopCargoDocumentDto[] = ride.ride_stops!.flatMap(stop => stop.ride_stop_cargoes!.filter(cargo => cargo.ride_cargo_id == cargoId));
    return stopCargoes;
  }

  private getClientDetails(ride: RideDocumentDto) {
    let obj = [] as ClientDetails[];

    for (const cargo of ride.ride_cargoes!.filter(b => b.student_id! > 0)) {
      const clientName = cargo.ride_cargo_extra_fields?.find(b => b.key_id == RideCargoExtraFields.PayingClientName)?.value;
      if (obj.filter(b => b.name == clientName).length == 0) {
        obj.push({ name: clientName } as ClientDetails);
      }
    }
    return obj;
  }

  private readonly rideExtraFieldReader = (key: number, ride: RideDocumentDto): any => ride.ride_extra_fields?.find(b => b.key_id == key)?.value;

  private readonly rideStopExtraFieldReader = (key: number, stopExtraField: RideStopExtraFieldDocumentDto[]) => stopExtraField?.find(b => b.key_id == key)?.value;

  private readonly rideCargoExtraFieldReader = (key: number, rideCargoExtraField: RideCargoExtraFieldDocumentDto[]) => rideCargoExtraField?.find(b => b.key_id == key)?.value ?? '';

  private readonly getRideJobStatus = (ride: RideDocumentDto) => ride.ride_jobs?.find(b => b.ride_job_extra_fields!.find(x => x.key_id == RideJobExtraFields.Type)?.value == 'Ride')?.ride_status_id;

  private getEnableLiveTracking(ride: RideDocumentDto): boolean {
    // Enable live tracking for all users when ride status is in LiveTrackingStatuses
    // This includes: InProgress, Completed, NoShow, CanceledLate, CanceledInAdvance
    return ride.id != null && (this.LiveTrackingStatuses.includes(ride.ride_status_id!) || this.LiveTrackingStatuses.includes(ride.job_status_id!));
  }
}
